import React, { useEffect, useRef, useState } from "react";

/*
 * Google AdSense in-list ads for the web results, mirroring the AdMob
 * native-ad cadence used in the app
 * (Mapli/components/bottomSheetComponents/locationLists.js: first ad after
 * the 3rd item, then every 5 items after that).
 *
 * Unlike AdMob's NativeAdView, standard AdSense doesn't hand back raw ad
 * fields to build a custom card with — Google renders the creative itself
 * inside the <ins> below.
 *
 * Ads always load as soon as a slot mounts — adConsent.js sets the
 * personalized/non-personalized flag globally (defaulting to
 * non-personalized) before this ever pushes, so there's nothing to gate on
 * here.
 */

const AD_CLIENT = "ca-pub-7165879298792979";
const AD_SLOT = "2080975606";

// Loaded lazily, only from AdSlot itself, instead of site-wide from
// index.html — this app is a single-page app with one index.html for every
// route, so a global <script> tag would load AdSense on every page
// (login, dashboards, empty/loading states, etc.), which is exactly what
// AdSense's "ads on screens without publisher-content" policy flags.
let scriptPromise = null;
function loadAdsenseScript() {
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve) => {
      const script = document.createElement("script");
      script.async = true;
      script.crossOrigin = "anonymous";
      script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${AD_CLIENT}`;
      script.onload = resolve;
      script.onerror = resolve; // fail silently — the unfilled-timeout below still hides the slot
      document.head.appendChild(script);
    });
  }
  return scriptPromise;
}

const AD_AFTER_FIRST = 3;
const AD_INTERVAL = 5;

// Inserts { __ad: true, key } markers into `items` at the same cadence as
// the app: after the 3rd item, then every 5 items thereafter. Real items are
// passed through untouched, so callers only need to branch on `__ad`.
export function interleaveAds(items) {
  const out = [];
  items.forEach((item, i) => {
    out.push(item);
    const seen = i + 1;
    const dueForAd = seen === AD_AFTER_FIRST || (seen > AD_AFTER_FIRST && (seen - AD_AFTER_FIRST) % AD_INTERVAL === 0);
    if (dueForAd) out.push({ __ad: true, key: `ad-${seen}` });
  });
  return out;
}

// Google marks the <ins> with data-ad-status="filled" | "unfilled" once the
// request resolves. Collapse the slot entirely on "unfilled" (or if nothing
// ever comes back — no fill, blocked request, account not approved yet) so
// visitors never see an empty box with just the "Annonse" label sitting in it.
const UNFILLED_TIMEOUT_MS = 4000;

// Fixed size instead of data-ad-format="auto": auto/full-width-responsive
// sizes the ad off the *container's* width, which for a wide list column
// can reserve several hundred px of height before Google even knows
// whether anything will fill it — that reserved block is what shows up as
// a big blank box. A plain 300x250 (one of the best-filling standard
// sizes) is fixed and predictable regardless of fill status.
const AD_WIDTH = 300;
const AD_HEIGHT = 250;

export function AdSlot({ label = "Annonse" }) {
  const insRef = useRef(null);
  const pushed = useRef(false);
  const [filled, setFilled] = useState(null); // null (pending) | true | false

  useEffect(() => {
    if (pushed.current || !insRef.current) return;
    pushed.current = true;
    const el = insRef.current;

    const checkStatus = () => {
      const status = el.getAttribute("data-ad-status");
      if (status) setFilled(status === "filled");
    };
    const observer = new MutationObserver(checkStatus);
    observer.observe(el, { attributes: true, attributeFilter: ["data-ad-status"] });
    const timeout = setTimeout(() => setFilled((f) => (f === null ? false : f)), UNFILLED_TIMEOUT_MS);

    loadAdsenseScript().then(() => {
      try {
        (window.adsbygoogle = window.adsbygoogle || []).push({});
      } catch {
        setFilled(false);
      }
    });

    return () => {
      observer.disconnect();
      clearTimeout(timeout);
    };
  }, []);

  if (filled === false) return null;

  return (
    <div style={{ padding: "18px 6px", borderBottom: "1px solid var(--border-hairline)" }}>
      <span style={{ display: "block", marginBottom: 8, font: "var(--type-caption)", color: "var(--ink-300)" }}>
        {label}
      </span>
      <div style={{ display: "flex", justifyContent: "center" }}>
        <ins
          ref={insRef}
          className="adsbygoogle"
          style={{ display: "inline-block", width: AD_WIDTH, height: AD_HEIGHT }}
          data-ad-client={AD_CLIENT}
          data-ad-slot={AD_SLOT}
        />
      </div>
    </div>
  );
}
